import { lazy, type ComponentType, type LazyExoticComponent } from "react";

/**
 * Recovery for stale code-split chunks. When a new build is deployed, the
 * hashed asset filenames change and the previous bundle's files are removed
 * from the CDN. A client that loaded the old `index.html` then fails to fetch a
 * lazy chunk it references — surfacing as "Failed to fetch dynamically imported
 * module: …/assets/<Name>-<hash>.js". The fix is a one-time full reload, which
 * pulls the fresh `index.html` pointing at the current hashes.
 */

const RELOAD_TS_KEY = "vadium:chunk-reload-ts";
/** Suppress a second reload within this window so a genuinely-missing chunk
 * (not just a stale hash) can't trap the page in a reload loop. */
const MIN_RELOAD_INTERVAL_MS = 10_000;

/** True for errors thrown when a dynamically imported chunk can't be fetched —
 * almost always a stale client after a deploy replaced the hashed asset. */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }
  return /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|dynamically imported module/i.test(
    error.message,
  );
}

/**
 * Trigger a single full-page reload to recover from a stale chunk, guarded so
 * repeated failures can't loop. Returns true if a reload was started.
 *
 * The reload is intentionally silent and immediate: a stale chunk means the
 * pane the user just opened cannot render at all, so reloading to the fresh
 * build is strictly better than showing a broken pane. In the common case this
 * fires on first navigation to a lazy pane (no in-progress work to lose); in the
 * less-common mid-session case (a background deploy while the app is open),
 * in-progress work such as a typed-but-unsent message or an active stream may be
 * discarded.
 *
 * Note: `sessionStorage` is tab-scoped, so each browser tab enforces the
 * interval independently — simultaneous reloads across tabs are possible but
 * self-correcting.
 */
export function reloadOnceForStaleChunk(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  let last = 0;
  try {
    last = Number(window.sessionStorage.getItem(RELOAD_TS_KEY)) || 0;
  } catch {
    // sessionStorage unavailable (e.g. private mode) — bail rather than risk a loop.
    return false;
  }
  if (Date.now() - last < MIN_RELOAD_INTERVAL_MS) {
    return false;
  }
  try {
    window.sessionStorage.setItem(RELOAD_TS_KEY, String(Date.now()));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}

/**
 * `React.lazy` wrapper that recovers from stale-chunk fetch failures. On a chunk
 * load error it triggers a guarded one-time reload and keeps the component in
 * its Suspense fallback while the page navigates away; any other error (or a
 * second failure after reload) propagates to the nearest error boundary.
 */
// Mirrors React's own `lazy` signature (generic over the component type) so prop
// types are preserved exactly; the `any` in the constraint matches React's types.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await factory();
    } catch (error) {
      if (isChunkLoadError(error) && reloadOnceForStaleChunk()) {
        // Never resolve: React holds the Suspense fallback until the reload lands.
        return new Promise<{ default: T }>(() => {});
      }
      throw error;
    }
  });
}
